import { c as _c } from "react/compiler-runtime";
import { isMaskedDob } from '@onefootprint/core';
import type { VaultData } from '@onefootprint/request-types';
import { isValid, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
type ReviewInformationProps = {
  data: VaultData;
  attributes: string[];
};

// A leap year, so a masked Feb 29 still formats.
const MASKED_YEAR_STAND_IN = 2000;

/**
 * A spelled-out month can't be misread as a day, so a DOB typed with day and month swapped shows up
 * here. A Verified-prefill DOB (`••••-MM-DD`) keeps its year masked.
 */
const formatDob = (value: string, language: string | undefined): string => {
  const isMasked = isMaskedDob(value);
  const date = parseISO(isMasked ? value.replace('••••', String(MASKED_YEAR_STAND_IN)) : value);
  if (!isValid(date)) return value;
  const formatted = new Intl.DateTimeFormat(language, {
    dateStyle: 'medium'
  }).format(date);
  return isMasked ? formatted.replace(String(MASKED_YEAR_STAND_IN), '••••') : formatted;
};
const Row = t0 => {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-caption-2 text-tertiary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-body-2 text-primary whitespace-pre-line" data-dd-privacy="mask">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col gap-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const maskSsn = (value: string): string => {
  const digits = value.match(/\d/g) ?? [];
  return `•••-••-${digits.slice(-4).join('')}`;
};

/** Read-only display of the identity data the user is confirming. */
export const ReviewInformation = t0 => {
  const $ = _c(47);
  const {
    data,
    attributes
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectData.confirm.labels"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation("idv2", t1);
  const dobRaw = data["id.dob"];
  let t2;
  if ($[1] !== dobRaw || $[2] !== i18n) {
    t2 = dobRaw ? formatDob(dobRaw, i18n.resolvedLanguage) : undefined;
    $[1] = dobRaw;
    $[2] = i18n;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dob = t2;
  const t3 = data["id.first_name"];
  const t4 = data["id.middle_name"];
  const t5 = data["id.last_name"];
  let t6;
  if ($[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = [t3, t4, t5].filter(Boolean);
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const name = t6.join(" ");
  const t7 = data["id.address_line1"];
  const t8 = data["id.address_line2"];
  const t9 = data["id.city"];
  const t10 = data["id.state"];
  const t11 = data["id.zip"];
  let t12;
  if ($[8] !== t10 || $[9] !== t11 || $[10] !== t9) {
    t12 = [t9, t10, t11].filter(Boolean);
    $[8] = t10;
    $[9] = t11;
    $[10] = t9;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const t13 = t12.join(", ");
  let t14;
  if ($[12] !== t13 || $[13] !== t7 || $[14] !== t8) {
    t14 = [t7, t8, t13].filter(Boolean);
    $[12] = t13;
    $[13] = t7;
    $[14] = t8;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const address = t14.join("\n");
  const ssn = data["id.ssn9"] || data["id.ssn4"];
  let t15;
  if ($[16] !== attributes || $[17] !== name || $[18] !== t) {
    t15 = attributes.includes("name") && name && <Row label={t(_temp)} value={name} />;
    $[16] = attributes;
    $[17] = name;
    $[18] = t;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== attributes || $[21] !== dob || $[22] !== t) {
    t16 = attributes.includes("dob") && dob && <Row label={t(_temp2)} value={dob} />;
    $[20] = attributes;
    $[21] = dob;
    $[22] = t;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== attributes || $[25] !== data || $[26] !== t) {
    t17 = attributes.includes("email") && data["id.email"] && <Row label={t(_temp3)} value={data["id.email"]} />;
    $[24] = attributes;
    $[25] = data;
    $[26] = t;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== attributes || $[29] !== data || $[30] !== t) {
    t18 = attributes.includes("phone_number") && data["id.phone_number"] && <Row label={t(_temp4)} value={data["id.phone_number"]} />;
    $[28] = attributes;
    $[29] = data;
    $[30] = t;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== address || $[33] !== attributes || $[34] !== t) {
    t19 = attributes.includes("full_address") && address && <Row label={t(_temp5)} value={address} />;
    $[32] = address;
    $[33] = attributes;
    $[34] = t;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== attributes || $[37] !== ssn || $[38] !== t) {
    t20 = (attributes.includes("ssn9") || attributes.includes("ssn4")) && ssn && <Row label={t(_temp6)} value={maskSsn(ssn)} />;
    $[36] = attributes;
    $[37] = ssn;
    $[38] = t;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = <section className="flex flex-col gap-5 border border-tertiary rounded p-4">{t15}{t16}{t17}{t18}{t19}{t20}</section>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
};
function _temp($) {
  return $.name;
}
function _temp2($_0) {
  return $_0.dob;
}
function _temp3($_1) {
  return $_1.email;
}
function _temp4($_2) {
  return $_2.phoneNumber;
}
function _temp5($_3) {
  return $_3.address;
}
function _temp6($_4) {
  return $_4.ssn;
}